// Shared user read-surface display helpers (status badge + the two chips +
// role/coverage labels), reused by the list columns, the peek, the detail page
// and the platform list so they read identically. The two chips are two TRUTHS
// (never mixed): Sign-in = how the account enters the WEB; Telegram = the chat
// link, per project (the column disappears when the project has no bot).

import { useTranslation } from 'react-i18next';
import { Badge, type BadgeProps } from '../ui/Badge.js';
import { icons, type Icon } from '../ui/icons.js';
import { formatDate } from '../../lib/format.js';
import type { LastActiveChannel, ScopeKind, UserStatus, SignIn, TelegramLinkState } from '@ems-app/schemas/users/responses';
import type { ExternalIdentity } from '@ems-app/schemas/users/telegram';
import type { TFunction } from 'i18next';

// All subtle — a multi-row table keeps a calm rhythm; bold is reserved for an
// at-most-one exceptional pill per region (COMPONENTS §Badge).
const STATUS_BADGE: Record<UserStatus, { variant: BadgeProps['variant']; appearance: BadgeProps['appearance'] }> = {
  active: { variant: 'success', appearance: 'subtle' },
  pending: { variant: 'info', appearance: 'subtle' },
  locked: { variant: 'warning', appearance: 'subtle' },
  // disabled = a recoverable, admin-set deactivation → warning (amber), echoed by
  // the detail lifecycle band. Not neutral (neutral is for calm info like demo/trial).
  disabled: { variant: 'warning', appearance: 'subtle' },
};

export function UserStatusBadge({ status, appearance }: { status: UserStatus; appearance?: BadgeProps['appearance'] }) {
  const { t } = useTranslation('users');
  const b = STATUS_BADGE[status];
  const label = t(`status.${status}`);
  return (
    <Badge variant={b.variant} appearance={appearance ?? b.appearance} title={appearance === 'dot' ? label : undefined}>
      {label}
    </Badge>
  );
}

// Chip 1 — Sign-in: how the account enters the WEB. Local/SSO calm as before;
// Invited = the setup link is out (info); Invite expired = the link lapsed and
// stays visible until re-sent (warning — the chase signal); None = no web access
// (neutral — deliberately NOT a claim about channels: chip 2 is that truth).
// All subtle — secondary info; strings shared with the platform surface.
const SIGN_IN_BADGE: Record<SignIn, BadgeProps['variant']> = {
  local: 'neutral',
  sso: 'info',
  invited: 'info',
  invite_expired: 'warning',
  none: 'neutral',
};

export function SignInBadge({ signIn }: { signIn: SignIn }) {
  const { t } = useTranslation('users');
  return (
    <Badge variant={SIGN_IN_BADGE[signIn]} appearance="subtle">
      {t(`signIn.${signIn}`)}
    </Badge>
  );
}

// Chip 2 — Telegram, explicitly named (the glyph rides IN the chip; a future
// Slack is a SECOND chip, never a rename): Linked (this project's bot can
// message them) · Unreachable (linked, but blocked the bot / deleted the chat /
// never opened THIS bot) · Invite sent / Invite expired (the CHAT invite — a
// different invitation than chip 1's) · Not linked.
const TELEGRAM_BADGE: Record<TelegramLinkState, BadgeProps['variant']> = {
  linked: 'success',
  unreachable: 'warning',
  invite_sent: 'info',
  invite_expired: 'warning',
  not_linked: 'neutral',
};

export function TelegramBadge({ state }: { state: TelegramLinkState }) {
  const { t } = useTranslation('users');
  return (
    <Badge variant={TELEGRAM_BADGE[state]} appearance="subtle">
      <icons.telegram size={12} aria-hidden />
      {t(`telegramState.${state}`)}
    </Badge>
  );
}

/** The detail read's `external_identity` → chip-2 state (mirrors the backend
 *  list derivation): null when the project has no bot (`reachable_here` null on
 *  a linked row = the no-bot answer — the chip disappears rather than claiming
 *  reachability nobody can have). */
export function telegramStateOf(identity: ExternalIdentity): TelegramLinkState | null {
  // No bot on THIS project => no chip, whatever the global identity says — the
  // exact rule the server applies to the list column (deriveTelegramState) and
  // the reason the column itself disappears (envelope has_telegram_bot).
  if (identity.bot_username === null) return null;
  switch (identity.state) {
    case 'linked':
      if (identity.reachable_here === null) return null;
      return identity.reachable_here ? 'linked' : 'unreachable';
    case 'pending':
      return 'invite_sent';
    case 'invite_expired':
      return 'invite_expired';
    default:
      return 'not_linked';
  }
}

// Last active — the max across channels, the channel named by its glyph (list)
// or its word (detail). The glyph map is the single home so the cell and the
// breakdown line can never disagree on which icon a channel wears.
export const LAST_ACTIVE_ICON: Record<LastActiveChannel, Icon> = {
  web: icons.serviceWeb,
  telegram: icons.telegram,
  api: icons.apiClient,
};

export function LastActiveCell({ at, channel, zone }: { at: string | null; channel: LastActiveChannel | null; /** The screen's one zone (ADR-082 §2) — the page's, hoisted. */ zone: string }) {
  const { t } = useTranslation('users');
  if (!at || !channel) return <span className="text-[var(--color-text-disabled)]">{t('role.none')}</span>;
  const Glyph = LAST_ACTIVE_ICON[channel];
  const word = t(`lastActiveChannel.${channel}`);
  return (
    <span
      className="flex items-center gap-1.5 text-[var(--color-text-secondary)]"
      title={`${formatDate(at, 'datetime', { timeZone: zone }) ?? ''} · ${word}`}
    >
      <Glyph size={14} aria-hidden />
      <span className="sr-only">{word}</span>
      {formatDate(at, 'date', { timeZone: zone })}
    </span>
  );
}

interface Membership {
  is_project_admin: boolean;
  role_name: string | null;
}

/** The role display string (also the sort value): admin → "Administrator";
 *  otherwise the role name, or an em-dash. (The roles UI is its own slice, so the
 *  role is plain text here — not yet an EntityRef.) */
export function roleLabel(t: TFunction, m: Pick<Membership, 'is_project_admin' | 'role_name'>): string {
  if (m.is_project_admin) return t('role.admin');
  return m.role_name ?? t('role.none');
}

/** The scope in one word — the list's column, its filter and the export (ADR-050 §Consequences): Administrator ·
 *  Whole project · Specific places · No scope yet. */
export function scopeLabel(t: TFunction, kind: ScopeKind): string {
  return t(`scope.kind.${kind}`);
}
